import './components/workspace/WorkspaceChrome.css';
import { useWorkspaceNavigation } from './components/workspace/useWorkspaceNavigation';
import React, { useCallback, useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from './components/Sidebar';
import { PageNotFound, RouteRecovery } from './components/RouteRecovery';
import Navbar from './components/Navbar';
import ErrorWidget from './components/ErrorWidget';
import { API_URL } from './config/api';
import { getAuthHeader, setSessionToken, invalidateLocalSession, getSessionGeneration, isSessionGenerationCurrent } from './config/auth';
import { isFeatureEnabled } from './config/featureFlags';
import BrandIcon from './components/BrandIcon';
import { identifyUser, resetAnalytics } from './lib/posthog';
import { writeCachedProfile } from './lib/profileSession';

// Lazy: public marketing / login surfaces (keep out of the main app chunk for LCP)
const LoginPageClean = lazy(() => import('./pages/LoginPageClean'));
const LoginPageHomeOld = lazy(() => import('./pages/LoginPageHomeOld'));
const LoginPageNew = lazy(() => import('./pages/LoginPageNew'));
const LoginPageBackup = lazy(() => import('./pages/LoginPageBackup'));
const DesignGuide = lazy(() => import('./pages/DesignGuide'));
const NikiOrchestrationPreview = lazy(() => import('./pages/NikiOrchestrationPreview'));
const NikiOrchestrationHybrid = lazy(() => import('./pages/NikiOrchestrationHybrid'));
const NikiMarketingAgentPreview = lazy(() => import('./pages/NikiMarketingAgentPreview'));
const NikiMarketingDashboardContent = lazy(() => import('./pages/NikiMarketingAgentPreview').then((module) => ({
  default: module.NikiMarketingDashboardContent,
})));

// Lazy: All authenticated pages (only downloaded when navigated to)
const Toolkit = lazy(() => import('./pages/Toolkit.jsx'));
const AnalysisHub = lazy(() => import('./pages/AnalysisHub.jsx'));
const RedesignPreview = lazy(() => import('./pages/RedesignPreview.jsx'));
const Automations = lazy(() => import('./pages/Automations.jsx'));
const Blogs = lazy(() => import('./pages/Blogs.jsx'));
const Ideas = lazy(() => import('./pages/Ideas.jsx'));
const Collection = lazy(() => import('./pages/Collection.jsx'));
const Agents = lazy(() => import('./pages/Agents.jsx'));
const ChatV2 = lazy(() => import('./pages/ChatV2.jsx'));
const Workspace = lazy(() => import('./pages/Workspace.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
const StudioPage = lazy(() => import('./pages/StudioPage'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const LandingTemp = lazy(() => import('./pages/LandingTemp'));
const TemplatesHub = lazy(() => import('./pages/Templates'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const RequireAdmin = lazy(() => import('./admin/RequireAdmin'));
const SlidesStudio = lazy(() => import('./slides/SlidesStudio'));
const SlideHarnessPage = lazy(() => import('./slides/SlideHarnessPage'));
const PublicFormPage = lazy(() => import('./forms/pages/PublicFormPage'));
const FormsWorkspacePage = lazy(() => import('./forms/pages/FormsWorkspacePage'));
const FormsNewPage = lazy(() => import('./forms/pages/FormsNewPage'));
const FormsEditorPage = lazy(() => import('./forms/pages/FormsEditorPage'));
const FormsPreviewPage = lazy(() => import('./forms/pages/FormsPreviewPage'));
const FormsSubmissionsPage = lazy(() => import('./forms/pages/FormsSubmissionsPage'));
const CampaignWorkspaceIndex = lazy(() => import('./campaign-workspace/CampaignWorkspaceIndex'));
const CampaignWorkspaceShell = lazy(() => import('./campaign-workspace/CampaignWorkspaceShell'));

const MarketingFallback = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-white">
    <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-3" />
    <p className="text-gray-400 font-bold text-xs uppercase tracking-widest">Loading...</p>
  </div>
);

const withMarketingSuspense = (element) => (
  <Suspense fallback={<MarketingFallback />}>{element}</Suspense>
);

// Page loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
    <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-3" />
    <p className="text-gray-400 font-bold text-xs uppercase tracking-widest">Loading...</p>
  </div>
);

const PROFILE_FETCH_TIMEOUT_MS = 5000;
const PROFILE_FETCH_RETRY_DELAYS_MS = [0, 750, 1750];
const OAUTH_CALLBACK_QUERY_KEYS = ['login_success', 'token', 'email'];

const clearOAuthCallbackQuery = () => {
  const callbackUrl = new URL(window.location.href);
  OAUTH_CALLBACK_QUERY_KEYS.forEach((key) => callbackUrl.searchParams.delete(key));
  window.history.replaceState(
    {},
    document.title,
    `${callbackUrl.pathname}${callbackUrl.search}${callbackUrl.hash}`,
  );
};

const AuthWrapper = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('isAuthenticated') === 'true');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const logout = useCallback(() => {
    invalidateLocalSession({ resetAnalytics });
    setIsAuthenticated(false);
    setLoading(false);
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const loginSuccess = searchParams.get('login_success');
    const emailFromUrl = searchParams.get('email');
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const fetchProfileOnce = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PROFILE_FETCH_TIMEOUT_MS);
      try {
        const url = `${API_URL}/api/auth/google/me`;
        const res = await fetch(url, {
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthHeader(),
          },
        });
        if (!res.ok) return null;
        return await res.json();
      } catch (err) {
        if (err.name === 'AbortError') {
          console.warn('Auth profile fetch timed out. Will retry with backoff.');
        }
        return null;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    const cacheProfile = (profile, sessionGen) => {
      // Drop late responses from a prior login/logout generation.
      if (!isSessionGenerationCurrent(sessionGen)) {
        return;
      }
      // Unconditional profile caching — analytics must not gate session fields.
      const hasId = writeCachedProfile(profile);
      if (!isSessionGenerationCurrent(sessionGen)) {
        return;
      }
      // Distinct-id only — never email/name to PostHog; skip when id absent.
      if (hasId) {
        identifyUser(profile.id);
      }
    };

    const refreshProfileInBackground = async () => {
      const sessionGen = getSessionGeneration();
      for (let i = 0; i < PROFILE_FETCH_RETRY_DELAYS_MS.length; i += 1) {
        if (!isSessionGenerationCurrent(sessionGen)) {
          return;
        }
        if (i > 0) {
          await delay(PROFILE_FETCH_RETRY_DELAYS_MS[i]);
        }
        if (!isSessionGenerationCurrent(sessionGen)) {
          return;
        }

        const profile = await fetchProfileOnce();
        if (!isSessionGenerationCurrent(sessionGen)) {
          return;
        }
        if (profile) {
          cacheProfile(profile, sessionGen);
          return;
        }
      }
      console.warn('Profile fetch failed after retries; continuing with cached session.');
    };

    // Round 8 Part A: identity comes from a signed session token (issued by
    // POST /api/auth/login or the Google OAuth callback), never a bare
    // email — the old ?email=... redirect was trusted forever after via a
    // client-echoed X-User-Email header, which the backend no longer
    // accepts at all (audit P0-1).
    const tokenFromUrl = searchParams.get('token');

    const initializeAuth = () => {
      if (loginSuccess === 'true' && tokenFromUrl) {
        setSessionToken(tokenFromUrl);
        localStorage.setItem('isAuthenticated', 'true');
        if (emailFromUrl) {
          localStorage.setItem('userEmail', emailFromUrl);
        }
        setIsAuthenticated(true);
        setLoading(false);

        // Clean URL early (the token must not linger in browser history)
        // and fetch the full profile in background.
        clearOAuthCallbackQuery();
        void refreshProfileInBackground();
        return;
      }

      if (loginSuccess === 'false') {
        clearOAuthCallbackQuery();
      }

      const hasSession = localStorage.getItem('isAuthenticated') === 'true';
      const storedToken = localStorage.getItem('sessionToken');
      if (storedToken && !hasSession) {
        console.warn(
          'Incomplete authentication state: Do not inject sessionToken manually. '
          + "Use 'Continue with this worktree' so the complete local session is established.",
        );
      }
      if (hasSession) {
        if (storedToken) {
          setIsAuthenticated(true);
          setLoading(false);
          void refreshProfileInBackground();
          return;
        }

        invalidateLocalSession({ resetAnalytics });
      }

      setIsAuthenticated(false);
      setLoading(false);
    };

    initializeAuth();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-4" />
        <p className="text-violet-600 font-black animate-pulse uppercase tracking-widest text-xs">Authenticating DNA...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (location.pathname === '/' || location.pathname === '') {
      return withMarketingSuspense(<NikiMarketingAgentPreview />);
    }
    return <Navigate to="/" replace />;
  }

  return React.cloneElement(children, { onLogout: logout });
};

const MainLayout = ({ onLogout }) => {
  const location = useLocation();
  const { isSidebarCollapsed, isMobile, toggleSidebar } = useWorkspaceNavigation(location.pathname);
  const isFormsEditorRoute = /^\/forms\/[^/]+\/edit\/?$/.test(location.pathname);
  const isAdminRoute = (location.pathname === '/admin' || location.pathname.startsWith('/admin/'));
  const isDashboardRoute = location.pathname === '/';
  const isCampaignWorkspaceRoute = location.pathname.startsWith('/campaign-workspaces');

  const featureRoute = (flagName, element) => (
    isFeatureEnabled(flagName) ? element : <Navigate to="/" replace />
  );

  if (isAdminRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <RequireAdmin>
          <AdminLayout />
        </RequireAdmin>
      </Suspense>
    );
  }

  return (
    <div className={`flex h-screen w-screen overflow-hidden relative ${isDashboardRoute ? 'bg-white' : 'bg-gray-100'}`}>
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        toggleCollapse={toggleSidebar}
        isMobile={isMobile}
        onLogout={onLogout}
      />

      {/* Mobile Overlay */}
      {isMobile && !isSidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-20 backdrop-blur-sm transition-opacity animate-in fade-in"
          onClick={toggleSidebar}
        />
      )}

      <main
        data-agent-view={location.pathname.startsWith('/studio') ? 'design' : location.pathname.startsWith('/slides') ? 'slides' : isCampaignWorkspaceRoute ? 'niki' : isFormsEditorRoute ? 'forms' : undefined}
        className={`
            isolate flex-1 flex flex-col h-full relative overflow-hidden transition-all duration-300 ease-in-out
            ${isMobile ? 'ml-0' : (isSidebarCollapsed ? 'ml-[4.5rem]' : 'ml-56')}
        `}
      >
        {(isDashboardRoute || isFormsEditorRoute || isCampaignWorkspaceRoute || location.pathname.startsWith('/studio') || location.pathname.startsWith('/slides')) && isMobile && isSidebarCollapsed && (
          <button
            onClick={toggleSidebar}
            className="absolute top-3 left-3 z-[60] p-2 rounded-lg bg-white/95 border border-gray-200 shadow-sm hover:bg-gray-50 text-gray-600 transition-colors"
            aria-label="Open navigation"
            title="Open navigation"
          >
            <Menu size={20} />
          </button>
        )}

        {/* Navbar: Fixed height, no shrink. HIDE in Studio & Slides */}
        {!location.pathname.startsWith('/studio') && !location.pathname.startsWith('/slides') && !isCampaignWorkspaceRoute && !isDashboardRoute && !isFormsEditorRoute && (
          <div className="shrink-0 w-full z-10">
            <Navbar onMenuClick={isMobile ? toggleSidebar : undefined} showMenuButton={isMobile} />
          </div>
        )}

        {/* Content Area: Fills remaining space, handles its own scroll */}
        <div className="flex-1 relative w-full overflow-hidden">
          <div className="absolute inset-0 overflow-y-auto">
            <RouteRecovery><Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={
                  isFeatureEnabled('nikiMarketingDashboard')
                    ? <NikiMarketingDashboardContent />
                    : <LandingTemp />
                } />
                <Route path="/workspace" element={featureRoute('workspace', <Workspace />)} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/chat/:conversationId" element={<ChatV2 />} />

                {/* Main Hubs */}
                <Route path="/templates" element={<TemplatesHub />} />
                <Route path="/toolkit" element={featureRoute('toolkit', <Toolkit />)} />
                <Route path="/analysis" element={featureRoute('analysisHub', <AnalysisHub />)} />
                <Route path="/redesign-preview" element={featureRoute('redesignPreview', <RedesignPreview />)} />
                <Route path="/automations" element={featureRoute('automations', <Automations />)} />
                <Route path="/ideas" element={featureRoute('ideas', <Ideas />)} />

                <Route path="/collection" element={<Collection />} />
                <Route path="/assets" element={<Collection />} />
                <Route path="/agents" element={featureRoute('agentsHub', <Agents />)} />
                <Route path="/agent-workspace" element={featureRoute('writerAgent', <ChatV2 />)} />
                <Route path="/campaigns" element={<Campaigns />} />
                <Route path="/projects" element={<Campaigns />} />
                <Route path="/campaign-workspaces" element={featureRoute('campaignWorkspaceMvp', <CampaignWorkspaceIndex />)} />
                <Route path="/campaign-workspaces/new" element={featureRoute('campaignWorkspaceMvp', <CampaignWorkspaceIndex />)} />
                <Route path="/campaign-workspaces/:workspaceId" element={featureRoute('campaignWorkspaceMvp', <CampaignWorkspaceShell />)} />
                <Route path="/campaign-workspaces/:workspaceId/campaign" element={featureRoute('campaignWorkspaceMvp', <CampaignWorkspaceShell />)} />
                <Route path="/campaign-workspaces/:workspaceId/assets" element={featureRoute('campaignWorkspaceMvp', <CampaignWorkspaceShell />)} />
                <Route path="/campaign-workspaces/:workspaceId/items/:itemId" element={featureRoute('campaignWorkspaceMvp', <CampaignWorkspaceShell />)} />
                <Route path="/forms" element={<FormsWorkspacePage />} />
                <Route path="/forms/new" element={<FormsNewPage />} />
                <Route path="/forms/:formId/edit" element={<FormsEditorPage />} />
                <Route path="/forms/:formId/preview" element={<FormsPreviewPage />} />
                <Route path="/forms/:formId/submissions" element={<FormsSubmissionsPage />} />
                {/* Visual Studio Integration: Needs strict 100% height without scroll bars */}
                <Route path="/studio" element={
                  <div className="absolute inset-0 w-full h-full bg-gray-950 overflow-hidden z-20">
                    <StudioPage />
                  </div>
                } />

                {/* Slides Studio Integration: Separate Route */}
                <Route path="/slides" element={
                  <div className="absolute inset-0 w-full h-full bg-gray-950 overflow-hidden z-20">
                    <SlidesStudio />
                  </div>
                } />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </Suspense></RouteRecovery>
          </div>
        </div>

        {/* Error Widget - Only visible in development */}
        {import.meta.env.DEV && <ErrorWidget />}
      </main>
    </div>
  );
};

const PublicBlogWrapper = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <nav className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <BrandIcon size={26} />
            <span className="text-2xl font-black tracking-tighter text-gray-900">Niki</span>
          </a>
          <a href="/" className="px-6 py-2.5 bg-violet-600 text-white rounded-full hover:bg-violet-700 transition-all font-bold text-sm shadow-md">
            Go to App
          </a>
        </div>
      </nav>
      <main className="flex-1 mt-20">
        <Suspense fallback={<PageLoader />}>
          <Blogs />
        </Suspense>
      </main>
      <footer className="py-12 border-t border-gray-100 text-center text-gray-500 font-medium text-sm">
        &copy; {new Date().getFullYear()} Niki Studio. All rights reserved.
      </footer>
    </div>
  );
};

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {/* /login aliases the public homepage; pre-promote page lives at /login-old */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/login-backup" element={withMarketingSuspense(<LoginPageBackup />)} />
        <Route path="/login-new" element={withMarketingSuspense(<LoginPageClean />)} />
        <Route path="/login-clean" element={withMarketingSuspense(<LoginPageClean />)} />
        <Route path="/homepage_backup" element={withMarketingSuspense(<LoginPageClean />)} />
        {/* Pre-30-Jul-2026 homepage backup (exact copy of LoginPage at time of promote) */}
        <Route path="/login-old" element={withMarketingSuspense(<LoginPageHomeOld />)} />
        {/* Prior /login-old occupant — LoginPageNew intermediate marketing page */}
        <Route path="/login-v2" element={withMarketingSuspense(<LoginPageNew />)} />
        <Route path="/design-guide" element={withMarketingSuspense(<DesignGuide />)} />
        <Route path="/niki-agent-preview" element={withMarketingSuspense(<NikiOrchestrationPreview />)} />
        <Route path="/niki-agent-homepage-v2" element={withMarketingSuspense(<NikiOrchestrationHybrid />)} />
        <Route path="/niki-marketing-agent-preview" element={
          isFeatureEnabled('nikiMarketingPreviews')
            ? withMarketingSuspense(<NikiMarketingAgentPreview />)
            : <Navigate to="/" replace />
        } />
        <Route path="/niki-marketing-agent-dashboard-preview" element={
          isFeatureEnabled('nikiMarketingPreviews')
            ? withMarketingSuspense(<NikiMarketingAgentPreview initialView="dashboard" />)
            : <Navigate to="/" replace />
        } />

        {/* Respondent Forms are deliberately public. They must not enter the
            authenticated application shell or inherit its redirect behavior. */}
        <Route path="/f/:formId" element={
          <Suspense fallback={<PageLoader />}>
            <PublicFormPage />
          </Suspense>
        } />

        {/* Deterministic visual-regression surface. Source-controlled fixture
            data only; never exposed by the production build. */}
        {import.meta.env.DEV && (
          <Route path="/slides-harness" element={
            <div className="absolute inset-0 w-full h-full bg-gray-950 overflow-auto z-20">
              <Suspense fallback={<PageLoader />}>
                <SlideHarnessPage />
              </Suspense>
            </div>
          } />
        )}

        {/* Public Blog Surface */}
        <Route path="/blogs/*" element={
          <Suspense fallback={<PageLoader />}>
            <PublicBlogWrapper />
          </Suspense>
        } />

        {/* `/` is public marketing when logged out; authenticated shell (LandingTemp at `/`) when logged in */}
        <Route path="/*" element={
          <AuthWrapper>
            <MainLayout />
          </AuthWrapper>
        } />
      </Routes>
    </Router>
  );
}

export default App;
